/**
 * ArticleBody
 * Renders a Help article's sanitized HTML body. Mirrors the backend whitelist
 * (p, br, h2, h3, b, i, strong, em, u, ul, ol, li, blockquote,
 * a[href,target,rel], figure[data-placement,data-size],
 * img[data-image-id,alt,src], figcaption) for defense in depth, and forces
 * safe, visibly-distinct external link behavior.
 *
 * Article Images (srg-backend ADR 0004): an img is kept only when it
 * references an uploaded image by `data-image-id`; any other image is removed,
 * and a src that is not that image's blob-storage URL is stripped. Clicking a kept image opens it full size
 * in a lightbox.
 *
 * Keep ALLOWED_TAGS in sync with srg-backend
 * api/services/help/helpers/sanitize.ts - a tag missing here is stripped at
 * render time even though it was stored successfully.
 */
import React, { useMemo, useState } from 'react';
import createDOMPurify from 'dompurify';
import { isArticleImageId, isArticleImageSource, isImagePlacement, isImageSize } from './articleImageHelpers';
import ArticleImageLightbox, { type ArticleImageLightboxImage } from './ArticleImageLightbox';
import styles from './ArticleBody.module.css';

export interface ArticleBodyProps {
  html: string;
  dataTestId?: string;
}

const ALLOWED_TAGS = [
  'p',
  'br',
  'h2',
  'h3',
  'b',
  'i',
  'strong',
  'em',
  'u',
  'ul',
  'ol',
  'li',
  'blockquote',
  'a',
  'figure',
  'img',
  'figcaption'
];
// `class` is deliberately absent: the styling class is applied by the hook
// below, which runs after attribute filtering, so author-supplied classes stay
// stripped rather than riding along with the one we add.
// `data-indent` carries the editor's paragraph indent level (see
// extensions/ParagraphIndent.ts). It is a bounded integer rather than an inline
// style, so indentation round-trips without ever allowing `style` through.
const ALLOWED_ATTR = [
  'href',
  'target',
  'rel',
  'data-indent',
  'data-placement',
  'data-size',
  'data-image-id',
  'alt',
  'src'
];

/** Which tag an Article Image attribute may sit on, and how its value is checked. */
interface ImageAttributeRule {
  tag: string;
  isValid: (value: string, node: Element) => boolean;
}

/**
 * Article Image attributes are only meaningful on one tag each, and their
 * values are validated - ALLOWED_ATTR alone would admit them anywhere, with
 * any value. An invalid Placement/Size is dropped, so CSS falls back to the
 * default layout; an invalid src is dropped, leaving the alt text.
 */
const IMAGE_ATTRIBUTE_RULES: Record<string, ImageAttributeRule> = {
  'data-placement': { tag: 'FIGURE', isValid: isImagePlacement },
  'data-size': { tag: 'FIGURE', isValid: isImageSize },
  'data-image-id': { tag: 'IMG', isValid: isArticleImageId },
  alt: { tag: 'IMG', isValid: () => true },
  src: { tag: 'IMG', isValid: (value, node) => isArticleImageSource(value, node.getAttribute('data-image-id')) }
};

const ARTICLE_IMAGE_SELECTOR = 'img[data-image-id]';

// A module-private DOMPurify instance. Hooks are registered per-instance, so
// registering on the shared singleton (DOMPurify.addHook) would fire this
// anchor rewriting on every sanitize() call anywhere in the app, whatever its
// config - silently forcing target="_blank" on other features' internal links.
const articlePurifier = createDOMPurify(window);

articlePurifier.addHook('uponSanitizeAttribute', (node, data) => {
  const rule = IMAGE_ATTRIBUTE_RULES[data.attrName];
  if (rule && (node.tagName !== rule.tag || !rule.isValid(data.attrValue, node))) {
    data.keepAttr = false;
  }
});

articlePurifier.addHook('afterSanitizeAttributes', (node) => {
  // Force every anchor to open safely in a new tab, regardless of what the
  // sanitized markup contained, and tag it for distinct link styling (AC 1056.7-8).
  if (node.tagName === 'A') {
    node.setAttribute('target', '_blank');
    node.setAttribute('rel', 'noopener noreferrer');
    node.classList.add(styles.articleLink);
  }

  if (node.tagName === 'IMG') {
    // Not an uploaded Article Image (or its id was invalid): never render it,
    // so articles cannot embed external images.
    if (!node.hasAttribute('data-image-id')) {
      node.remove();
      return;
    }
    // Keyboard access to the lightbox. Added after filtering, like the
    // anchor class above, so authors cannot supply their own tabindex.
    node.setAttribute('tabindex', '0');
    node.classList.add(styles.articleImage);
  }
});

/** The Article Image a click or key press targeted, if it can be enlarged. */
const getTargetImage = (target: EventTarget): ArticleImageLightboxImage | null => {
  if (!(target instanceof HTMLImageElement) || !target.matches(ARTICLE_IMAGE_SELECTOR) || !target.src) return null;
  return { src: target.src, caption: target.alt };
};

const ArticleBody: React.FC<ArticleBodyProps> = ({ html, dataTestId = 'article-body' }) => {
  const sanitizedHtml = useMemo(
    () => articlePurifier.sanitize(html, { ALLOWED_TAGS, ALLOWED_ATTR }),
    [html]
  );
  const [lightboxImage, setLightboxImage] = useState<ArticleImageLightboxImage | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    const image = getTargetImage(event.target);
    if (image) setLightboxImage(image);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    const image = getTargetImage(event.target);
    if (!image) return;
    event.preventDefault();
    setLightboxImage(image);
  };

  return (
    <>
      {/* Delegated handlers: the images live inside the sanitized HTML, and each
          one is itself focusable (tabindex) for keyboard users. */}
      <div
        className={styles.articleBody}
        data-test-id={dataTestId}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
      />
      <ArticleImageLightbox
        image={lightboxImage}
        onClose={() => setLightboxImage(null)}
        dataTestId={`${dataTestId}-lightbox`}
      />
    </>
  );
};

export default ArticleBody;
